import React, { useContext, useState, useEffect, lazy, Suspense } from "react";
import { Icon, Stack, Text } from "@fluentui/react";
import {
  getConsumptionAPI,
  getConsumptionAPILocal,
  postConsumptionAPI,
} from "../../../utils/httpUtils";
import { IServiceContext, ServiceContext } from "@msx/platform-services";
import ".././CatalogPage.css";
import { InjectedIntlProps, injectIntl } from "react-intl";
import ShimmerLoader from "../../main/Services/ShimmerLoader";
import ErrorComponent from ".././ErrorComponent";
import * as Constants from "../../../utils/constants";
import {
  SETChileComponentName,
  SETHasServiceRequestTypeLogData,
  SETIsCustomeLog,
  SETLogEventData,
  SETServiceRequestTypeLogData,
  getIsAiSearchedTile,
  getSelectedCategories,
  setAddTile,
  setIsAiSearchedTile,
  setIsLoader,
} from "../../../../core/store";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { lazyRetry } from "../../../utils/utils";
import "./V2_Container.css";
import { IFilterCategory } from "../../filterPanelCatalog/FilterPanel.types";

const Tile = lazy(() => lazyRetry(() => import("./V2_TileComponent")));
const SearchComponent = lazy(() =>
  lazyRetry(() => import("./V2_SearchComponent"))
);
const FilterLeftPanel = lazy(() =>
  lazyRetry(() =>
    import("../../filterPanelCatalog/V2component_Filter/V2_FilterPanel").then(
      (module) => ({ default: module.V2_FilterLeftPanel })
    )
  )
);

const mimeTypeMap = {
  'image/png': 'png',
  'image/jpeg': 'jpg'
};

const toCamelCase = (str: string) => {
  return str
    .trim()
    .toLowerCase()
    .replace(/\s+(.)/g, (_, char) => char.toUpperCase()); // Capitalizes first letter after space
};

export const fetchAllFiltersOptions = async (context: Partial<IServiceContext>) => {
  try {
    if (context?.authClient) {
      // Call all APIs in parallel
      const [areaResponse, orgSummaryResponse, filterResponse] = await Promise.all([
        getConsumptionAPI("/api/V2_Area/GetAll", context.authClient),
        getConsumptionAPI("/api/V2_OrgSummary/GetAll", context.authClient),
        getConsumptionAPI("/api/Services/GetCatalogFilters", context.authClient),
      ]);

      // Process Areas response
      const areaData = areaResponse?.status === 200 && areaResponse?.data
        ? areaResponse.data?.map((item) => ({
          key: item.id,
          ID: item.id,
          text: item.name,
          name: item.name,
        }))
        : [];

      // Process Org Summary response
      const orgSummaryData = orgSummaryResponse?.status === 200 && orgSummaryResponse?.data
        ? orgSummaryResponse.data?.map((item) => ({
          key: item.id,
          ID: item.id,
          text: item.name,
          name: item.name,
        }))
        : [];

      // Process Filter Options response
      const filterOptionsData = filterResponse?.status === 200 && filterResponse?.data
        ? Object.keys(filterResponse.data)?.map((key) => {
          const formattedKey = toCamelCase(key); // Convert to camelCase
          return {
            name: formattedKey,
            key: formattedKey,
            options: filterResponse.data[key]?.map(
              (item: { ID: number; Name: string }) => ({
                ID: item.ID,
                name: item.Name,
                selected: false,
              })
            ),
          };
        })
        : [];

      // Update state once with all data at the same time
      return {
        Areas: areaData,
        orgSummaryOptions: orgSummaryData,
        statefilterOptions: filterOptionsData,
      };
    }
    return {
      Areas: [],
      orgSummaryOptions: [],
      statefilterOptions: [],
    };
  } catch (error) {
    console.error("An error occurred while fetching data.", error);
    return {
      Areas: [],
      orgSummaryOptions: [],
      statefilterOptions: [],
    };
  }
};

export interface CatalogPageProp extends InjectedIntlProps {
  setActiveTabKey?: any;
  setIrisContext?: any;
  userId: any;
  setAddServiceId: any;
  resetCatalogIrisContext: any;
  catalogServicesResponse: any;
  setServicesAdded: any;
  isLoading: any;
  setSearchTerm?: any;
  setSingleServiceAdded: any;
  handleIrisLoading: any;
  currentUserData: any;
  hasFetchedData: any;
}

// CatalogPage Component
const CatalogComponent: React.FC<CatalogPageProp> = (props) => {
  const [searchValue, setSearchValue] = useState("");
  const [dataTile, setDataTile] = useState([]);
  const [originalDataTile, setOriginalDataTile] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [resultError, setResultError] = useState("");
  const [errorData, setErrorData] = useState([]);
  const userId = props.userId;
  const context = useContext(ServiceContext);
  const [isLoading, setIsLoading] = useState(false);
  const SelectedCategoriesData = useSelector(getSelectedCategories);
  const isAiSearchedTile = useSelector(getIsAiSearchedTile);
  const [imageSearchSuggetions, setImageSearchSuggetions] = useState([]);
  const [isImageSafe, setisImageSafe] = useState("");
  const [isServiceFound, setServiceFound] = useState(true);
  const [imageSearchDescription, setImageSearchDescription] = useState("");

  const reduxDispatch = useDispatch();
  useEffect(() => {
    reduxDispatch(setIsLoader(false));
    const fetchData = async () => {
      try {
        if (context) {
          setIsLoading(true);
          // Fetch data from parent shell component
          const response = props.catalogServicesResponse;
          if (response?.data) {
            let result = checkResponse(response);
            if (result === "success") {
              setResultError("success");
              setOriginalDataTile(response.data);

              if (!isAiSearchedTile) {
                setDataTile(response.data);
              }
            } else {
              setResultError("error");
              setErrorData(response.data);
            }
          } else {
            throw new Error("Failed to fetch data.");
          }
        }
      } catch (error) {
        console.error("An error occurred while fetching data.", error);
      } finally {
        setIsLoading(false);
      }
    };

    props.setServicesAdded(false);
    fetchData();
  }, [props.catalogServicesResponse]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    reduxDispatch(setIsAiSearchedTile(false));
    fetchAllAreasAndOrgSummary();
  }, []);

  const [filtersData, setFiltersData] = useState<{
    Areas: any;
    orgSummaryOptions: any;
    statefilterOptions: IFilterCategory[];
  }>();

  // const fetchAllAreasAndOrgSummary = async () => {
  //   try {
  //     if (context && context.authClient) {
  //       // Fetch Areas
  //       const areaResponse = await getConsumptionAPI(
  //         "/api/V2_Area/GetAll",
  //         context.authClient
  //       );
  //       if (areaResponse?.status === 200 && areaResponse?.data) {
  //         let areaData = areaResponse.data.map((item) => ({
  //           key: item.id,
  //           ID: item.id,
  //           text: item.name,
  //           name: item.name,
  //         }));
  //         setAreas(areaData);
  //       }

  //       // Fetch Org Summary
  //       const orgSummaryResponse = await getConsumptionAPI(
  //         "/api/V2_OrgSummary/GetAll",
  //         context.authClient
  //       );
  //       if (orgSummaryResponse?.status === 200 && orgSummaryResponse?.data) {
  //         let orgSummaryData = orgSummaryResponse.data.map((item) => ({
  //           key: item.id,
  //           ID: item.id,
  //           text: item.name,
  //           name: item.name,
  //         }));
  //         setOrgSummaryOptions(orgSummaryData);
  //       }
  //     }
  //   } catch (Exception) {
  //     console.error(Exception);
  //   }
  // };

  const fetchAllAreasAndOrgSummary = async () => {
    try {
      const filterOtions = await fetchAllFiltersOptions(context);
      // Update state once with all data at the same time
      setFiltersData(filterOtions);
    } catch (error) {
      console.error("An error occurred while fetching data.", error);
    }
  };

  // Function to check the response status
  const checkResponse = (response) => {
    switch (response.status) {
      case 200:
      case 201:
      case 204:
        return "success";
      case 401:
      default:
        return "error";
    }
  };

  // Function to set the active tab key in Catalog
  const setActiveTabKeyinCatalog = () => {
    props.setActiveTabKey("0");
  };

  // Event handler for navigating to the dashboard
  const handleGoToDashboardButtonClick = () => {
    setActiveTabKeyinCatalog();
    setChatBotContextFromCatalog("create support ticket");
    props.handleIrisLoading(false);

    // Start Event Logging //
    reduxDispatch(SETChileComponentName("Catalog"));

    reduxDispatch(SETHasServiceRequestTypeLogData(true));
    reduxDispatch(
      SETLogEventData({
        elementName: "Go to My Help Workspace",
        elementId: "",
        action: "click",
        message: `User click on Go to My Help Workspace`,
        profileSetting: `v2`,
      })
    );
    reduxDispatch(
      SETServiceRequestTypeLogData({
        SearchTerm: searchValue,
        profileSetting: `v2`,
      })
    );
    reduxDispatch(SETIsCustomeLog(true));
    // End Event Logging //
  };

  // Function to set the Iris context from Catalog
  const setChatBotContextFromCatalog = (irisContext) => {
    props.setIrisContext(irisContext);
    props.setSearchTerm(searchValue);
  };

  // Effect to reset Iris context when needed
  useEffect(() => {
    if (props.resetCatalogIrisContext) {
      props.setIrisContext(null);
    }
  }, [props.resetCatalogIrisContext]);

  // Function to filter suggestions based on the search input
  const filterSuggestions = async (input) => {
    const lowercasedInput = input.toLowerCase();
    let filtered = [];
    if (input.length > 2) {
      try {
        const dataSearch =
          props.currentUserData?.isAiUser &&
            props.currentUserData?.isAiServicesEnabled
            ? await getAiSearchResult(input)
            : await getSearchResult(input);
        if (dataSearch) {
          const searchNames = dataSearch.map((item) => item.name);
          filtered = dataTile.filter((tile) => searchNames.includes(tile.name));
        } else {
          filtered = dataTile
            .filter((tile) => tile.name.toLowerCase().includes(lowercasedInput))
            .slice(0, 5);
        }
      } catch (error) {
        console.error("Error fetching search data:", error);
      }
      if (filtered.length > 0) {
        return filtered;
      }
    }

    const exactMatch = dataTile.find(
      (tile) => tile.name.toLowerCase() === lowercasedInput
    );
    return exactMatch && input.length > 2 ? [exactMatch] : [];
  };

  // Event handler for search box input
  const handleSearch = async (event) => {
    reduxDispatch(setAddTile(false));
    const newValue = event?.target?.value || "";
    const newSearchValue = newValue ? newValue.toString() : "";
    setSearchValue(newSearchValue);
    !props.currentUserData?.isAiServicesEnabled
      ? setSuggestions(await filterSuggestions(newValue))
      : setSuggestions([]);
    setSelectedSuggestion(null);
    if (!newValue) {
      setSelectedSuggestion(null);
      setDataTile(originalDataTile);
    }
  };

  // INC-845889863 safety net: return every catalog tile whose visible text literally
  // contains the query. Runs against originalDataTile (the user's own catalog set), so it
  // is independent of AI top-K ranking and of the SQL proc's INNER JOINs. This guarantees
  // superstring cases like "Japan" always surfacing the "Japanese Support" tile.
  const catalogSubstringMatches = (searchTerm) => {
    const q = (searchTerm ?? "").toString().toLowerCase().trim();
    if (!q) return [];
    return originalDataTile.filter((tile) => {
      const haystack = [tile?.name, tile?.AboutService, tile?.TileName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  };

  const handleAiSearch = async (searchTerm) => {
    try {
      // INC-845889863: AI semantic search returns a truncated top-K set and can omit
      // valid literal matches (e.g. "Japanese" when searching "Japan"). Union the AI
      // relevance-ranked results with the deterministic substring (LIKE) results so no
      // literal match is ever dropped.
      const [aiSearchData, literalSearchData] = await Promise.all([
        getAiSearchResult(searchTerm),
        getSearchResult(searchTerm),
      ]);

      const aiIds =
        Array.isArray(aiSearchData)
          ? aiSearchData.map((item: any) => item.id)
          : [];

      const literalIds = Array.isArray(literalSearchData)
        ? literalSearchData.map((item: any) => item.id)
        : [];

      // AI-ranked ids first (preserve relevance order), then any literal matches AI missed.
      const unionIds = [
        ...aiIds,
        ...literalIds.filter((id) => !aiIds.includes(id)),
      ];

      let filteredTiles = unionIds
        .map((id) => originalDataTile.find((item) => item.id === id))
        .filter(Boolean);

      // Client-side safety net: append any catalog tile literally matching the query
      // that the AI/proc union missed (guarantees the "Japanese" tile under "Japan").
      const existingIds = new Set(filteredTiles.map((tile) => tile.id));
      const substringExtra = catalogSubstringMatches(searchTerm).filter(
        (tile) => !existingIds.has(tile.id)
      );
      filteredTiles = [...filteredTiles, ...substringExtra];

      setServiceFound(filteredTiles.length > 0);
      setDataTile(filteredTiles);
      setSuggestions(filteredTiles);
    } catch (error) {
      setSuggestions([]);
    }
  };

  const handleNonAiSearch = async (searchTerm) => {
    try {
      const dataSearch = await getSearchResult(searchTerm);
      const literalIds = Array.isArray(dataSearch)
        ? dataSearch.map((item) => item.id)
        : [];

      // Match against the full catalog (originalDataTile) and preserve the
      // relevance order returned by the search proc so no literal match is lost.
      let filteredTiles = literalIds
        .map((id) => originalDataTile.find((tile) => tile.id === id))
        .filter(Boolean);

      // Safety net: append any catalog tile literally matching the query that the
      // proc missed (e.g. dropped by an inactive service mapping).
      const existingIds = new Set(filteredTiles.map((tile) => tile.id));
      const substringExtra = catalogSubstringMatches(searchTerm).filter(
        (tile) => !existingIds.has(tile.id)
      );
      filteredTiles = [...filteredTiles, ...substringExtra];

      setServiceFound(filteredTiles.length > 0);
      setDataTile(filteredTiles);
      setSuggestions([]);
    } catch (error) {
      setSuggestions([]);
    }
  };

  const filterTilesBySearch = async (searchTerm) => {
    if (
      props.currentUserData?.isAiUser &&
      props.currentUserData?.isAiServicesEnabled
    ) {
      await handleAiSearch(searchTerm);
    } else {
      await handleNonAiSearch(searchTerm);
    }
  };

  const getSearchResult = async (query) => {
    try {
      setIsLoading(true);
      if (context) {
        let data = {
          mcemFilter:
            SelectedCategoriesData["mcemStage"] &&
              SelectedCategoriesData["mcemStage"].length > 0
              ? SelectedCategoriesData["mcemStage"].toString()
              : null,
          categoryFilter:
            SelectedCategoriesData["Category"] &&
              SelectedCategoriesData["Category"].length > 0
              ? SelectedCategoriesData["Category"].toString()
              : null,
          orgSummaryFilter:
            SelectedCategoriesData["orgSummaryOptions"] &&
              SelectedCategoriesData["orgSummaryOptions"].length > 0
              ? SelectedCategoriesData["orgSummaryOptions"].toString()
              : null
        };

        const response = await postConsumptionAPI(
          `/api/V2_Service/GetAllServicesWithSearch/${userId}?query=${query}`,
          data,
          context.authClient
        );
        if (response?.data) {
          setIsLoading(false);
          return response.data;
        } else {
          setIsLoading(false);
          throw new Error("Failed to fetch data.");
        }
      }
    } catch (error) {
      setIsLoading(false);
      console.error("An error occurred while fetching data.", error);
    }
  };

  function getSelectedFilterOptions(filterOption, selectedFilterOptionsIDs) {
    if (selectedFilterOptionsIDs.length) {
      return selectedFilterOptionsIDs
        .map(selectedID => filterOption.find(f => f.key === selectedID)?.text)
        .filter(Boolean)
        .join(", ");
    } else {
      return filterOption.map(filter => filter.name).join(",");
    }
  }

  function getSelectedMCEMStageFilterOptions(filterOption, selectedFilterOptionsIDs) {
    if (selectedFilterOptionsIDs?.length) {
      return filterOption
        .filter(option => selectedFilterOptionsIDs.includes(option.ID))
        .map(option => option.ID)
        .join(",");
    } else {
      return filterOption.map(filter => filter.ID).join(",");
    }
  }

  const getAiSearchResult = async (query) => {
    try {
      setIsLoading(true);
      if (context) {
        const selectedAreaFilterOptions = Array.isArray(SelectedCategoriesData["Areas"]) ? SelectedCategoriesData["Areas"].join(',') : '';
        const OrgSummaryOptions = Array.isArray(SelectedCategoriesData["orgSummaryOptions"]) ? SelectedCategoriesData["orgSummaryOptions"].join(',') : '';

        const mcemStageFilters = filtersData["statefilterOptions"].find(filter => filter.key === "mcemStage").options;
        const McemStageOptions = getSelectedMCEMStageFilterOptions(mcemStageFilters, SelectedCategoriesData["mcemStage"]);

        let data = {
          Area: selectedAreaFilterOptions,
          OrgSummary: OrgSummaryOptions,
          McemStage: McemStageOptions
          // CategoryFilter: SelectedCategoriesData["Category"] && SelectedCategoriesData["Category"].length > 0 ? SelectedCategoriesData["Category"].toString() : null,
        };

        const response = await postConsumptionAPI(
          `/api/V2_Service/GetServicesWithAiSearch?query=${query}`,
          data,
          context.authClient
        );
        if (response?.data) {
          setIsLoading(false);
          if (Array.isArray(response?.data) && response.data.length > 0) {
            reduxDispatch(setIsAiSearchedTile(true));
          } else {
            reduxDispatch(setIsAiSearchedTile(false)); // Optional: Reset to false if no data
          }
          return response.data;
        } else {
          setIsLoading(false);
          throw new Error("Failed to fetch data.");
        }
      }
    } catch (error) {
      setIsLoading(false);
      console.error(
        "An error occurred while fetching AI-based search data.",
        error
      );
    }
  };

  const handleOnSearch = async (newValue) => {
    // Start Event Logging //
    reduxDispatch(SETChileComponentName("Catalog"));

    reduxDispatch(SETHasServiceRequestTypeLogData(true));
    reduxDispatch(
      SETLogEventData({
        elementName: "Search term searched",
        elementId: "",
        action: "Key Press",
        message: `User Key Press on Search term search`,
        profileSetting: `v2`,
      })
    );
    reduxDispatch(
      SETServiceRequestTypeLogData({
        SearchTerm: newValue,
        profileSetting: `v2`,

      })
    );
    reduxDispatch(SETIsCustomeLog(true));
    // End Event Logging //

    await filterTilesBySearch(newValue);
  };

  // Event handler for suggestion click
  const handleSuggestionClick = async (suggestion) => {
    // Start Event Logging //
    reduxDispatch(SETChileComponentName("Catalog"));

    reduxDispatch(SETHasServiceRequestTypeLogData(true));
    reduxDispatch(
      SETLogEventData({
        elementName: "Search suggestion selection",
        elementId: "",
        action: "click",
        message: `User click on Search suggestion selection`,
        profileSetting: `v2`,
      })
    );
    reduxDispatch(
      SETServiceRequestTypeLogData({
        ServiceName: suggestion.name,
        RequestType: "",
        TileName: suggestion.tileName,
        IsNonIrisService: suggestion.isNonIRISService ? "true" : "false",
        IrisUtterance: suggestion.iriS_Utterance,
        profileSetting: `v2`,
      })
    );
    reduxDispatch(SETIsCustomeLog(true));
    // End Event Logging //
    setSelectedSuggestion(suggestion);
    setSearchValue(suggestion.name);
    setSuggestions([]);
    setImageSearchSuggetions([]);
  };

  const handleSuggestionKeyPress = (e, suggestion) => {
    if (e.key === "Enter") {
      handleSuggestionClick(suggestion);
    }
  };

  const handleImageSearch = (file?: any, searchText?: any, base64String?: any) => {
    setIsLoading(true);
    handleimageAiSearch(file, searchText, base64String);

  }

  const handleCancelImageSearch = () => {
    setSuggestions([]);
    setSelectedSuggestion(null);
    setDataTile(originalDataTile);
    setisImageSafe("");
  }

  function base64ToFile(base64String, filename, mimType) {
    const byteString = atob(base64String.split(',')[1]);
    const ab = new ArrayBuffer(base64String.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new File([ab], filename, { type: mimType });
  }

  const handleimageAiSearch = async (file?: any, searchText?: any, base64String?: any) => {
    try {
      const selectedAreaFilterOptions = Array.isArray(SelectedCategoriesData["Areas"]) ? SelectedCategoriesData["Areas"].join(',') : '';
      const OrgSummaryOptions = Array.isArray(SelectedCategoriesData["orgSummaryOptions"]) ? SelectedCategoriesData["orgSummaryOptions"].join(',') : '';
      const mcemStageFilters = filtersData["statefilterOptions"].find(filter => filter.key === "mcemStage").options;
      const McemStageOptions = getSelectedMCEMStageFilterOptions(mcemStageFilters, SelectedCategoriesData["mcemStage"]);

      const formData = new FormData();
      if (file != null) {
        formData.append("File", file);
      } else {
        const mimeType = base64String.split(';')[0].split(':')[1];
        const filename = Date.now() + "." + mimeTypeMap[mimeType]
        const sreenshotfile = base64ToFile(base64String, filename, mimeType);
        formData.append("File", sreenshotfile);
      }

      // Call ImageContentSafety API
      const safetyApiUrl = '/api/ImageProcessing/ImageContentSafety';
      const safetyResponse = await postConsumptionAPI(safetyApiUrl, formData, context.authClient);

      if (safetyResponse?.data?.isSafe == true) {
        setisImageSafe("");
        const summarizeApiUrl = '/api/ImageProcessing/GetImageContent';
        const summarizeResponse = await postConsumptionAPI(summarizeApiUrl, formData, context.authClient);

        const summary = summarizeResponse.data;

        // calling another API for showing top 3 recommended services
        const recommendedServices = '/api/ImageProcessing/GetServicesForImageContent';
        const postData = {
          imageSummary: summary,
          prompt: searchText,
          area: selectedAreaFilterOptions,
          orgSummary: OrgSummaryOptions,
          mcemStage: McemStageOptions,
        };

        const recommendedResponse = await postConsumptionAPI(recommendedServices, postData, context.authClient)
        var services = recommendedResponse.data.services;

        let filteredTiles = [];
        if (recommendedResponse?.data?.services.length > 0) {
          const searchIds = recommendedResponse.data.services.map(item => item.id);
          filteredTiles = searchIds.map(id => originalDataTile.find(item => item.id === id));
        } else {
          const imageDescription = recommendedResponse?.data?.imageDescription || "";
          filteredTiles = originalDataTile.filter((tile) =>
            imageDescription.includes(tile.name)
          );
        }

        const imageDescription = recommendedResponse?.data?.imageDescription || "";
        LogImageSearchDescription(imageDescription, searchText);
        setImageSearchDescription(imageDescription);
        setDataTile(filteredTiles);
        setSuggestions([]);
        setImageSearchSuggetions(recommendedResponse.data);
        setIsLoading(false);
      } else {
        setisImageSafe("Sorry, the image you uploaded is not safe. Please upload a valid image.");
      }
    }
    catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  }

  function LogImageSearchDescription(description: any, userQuery: any) {
    reduxDispatch(SETChileComponentName("ImageSearch"));

    reduxDispatch(SETHasServiceRequestTypeLogData(true));
    reduxDispatch(
      SETLogEventData({
        elementName: "image-based search",
        elementId: "",
        action: "Image Search",
        message: `User uploaded image for search`,
        profileSetting: `v2`,
      })
    );
    reduxDispatch(
      SETServiceRequestTypeLogData({
        ImageDescription: "user Query: " + userQuery + ", Description: " + description,
        profileSetting: `v2`,
      })
    );
    reduxDispatch(SETIsCustomeLog(true));

  }

  const displayTiles = selectedSuggestion ? [selectedSuggestion] : dataTile;
  useEffect(() => {
    const filteredData = originalDataTile.filter((tile) => {
      // Check if the tile matches any selected filter
      return (
        selectedFilters.length === 0 || selectedFilters.includes(tile.category)
      );
    });

    if (!isAiSearchedTile) {
      setDataTile(filteredData);
    }
  }, [selectedFilters, originalDataTile]);

  // Function to handle filter selection
  const handleFilterSelect = (filter) => {
    // Toggle filter selection
    reduxDispatch(setAddTile(false));
    const updatedFilters = selectedFilters.includes(filter)
      ? selectedFilters.filter((f) => f !== filter)
      : [...selectedFilters, filter];

    setSelectedFilters(updatedFilters);
  };

  useEffect(() => { }, [SelectedCategoriesData]);

  return (
    <Stack style={{ marginTop: "25px" }}>
      {props.isLoading && <ShimmerLoader count={15} />}
      {!props.isLoading && (
        <Stack>
          {resultError === "success" ? (
            <Suspense fallback={<></>}>
              <Stack horizontal verticalAlign="start">
                <div className="ms-Grid-col ms-lg3 LeftPanelfilter">
                  <SearchComponent
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    handleSearch={handleSearch}
                    suggestions={suggestions}
                    handleSuggestionClick={handleSuggestionClick}
                    handleSuggestionKeyPress={handleSuggestionKeyPress}
                    selectedSuggestion={selectedSuggestion}
                    handleOnSearch={handleOnSearch}
                    handleImageSearch={handleImageSearch}
                    isLoading={isLoading}
                    currentUserData={props.currentUserData}
                    imageSearchSuggetions={imageSearchSuggetions}
                    handleCancelImageSearch={handleCancelImageSearch}
                    isImageSafe={isImageSafe}
                    setServiceFound={setServiceFound}
                    isServiceFound={isServiceFound}
                  />
                  <FilterLeftPanel
                    selectedFilters={selectedFilters}
                    handleFilterSelect={handleFilterSelect}
                    Areas={filtersData?.Areas}
                    orgSummaryOptions={filtersData?.orgSummaryOptions}
                    currentUserData={props.currentUserData}
                    IsMyAreaSupportEnabled={props.currentUserData.isAreaSupportEnabled}
                    statefilterOptions={filtersData?.statefilterOptions || []}
                  />
                </div>
                <div className="ms-Grid-col ms-lg9 rightPanelfilter">
                  {/* <Stack horizontalAlign="center" style={{width:'100%'}}>
            <Suspense fallback={<></>}> */}

                  {/* </Suspense>
          </Stack> */}
                  <div className="scroll_conatiner"
                    style={{ height: 'calc(90vh - 100px)', width: "100%" }}>
                    <div id="v2_Container" className="v2_Container"></div>
                    <Tile
                      dataTile={displayTiles}
                      userId={userId}
                      setAddServiceId={props.setAddServiceId}
                      setSingleServiceAdded={props.setSingleServiceAdded}
                      currentUserData={props.currentUserData}
                      hasFetchedData={props.hasFetchedData}
                      userQuery={searchValue}
                      imageDescription={imageSearchDescription}
                    />
                    <Stack
                      className="catalogBottomContainer"
                      aria-label="Unable to find what you are looking for?"
                      tokens={{ childrenGap: 10 }}
                      style={{ display: 'flex' }}
                    >
                      {/* <div style={{ flex: '0 0 auto' }}>
                      <img src={catalog_page_unable_to_find_icon} alt="Icon" className="catalogIcon"
                        style={{ height: "60px", width: "auto" }}
                      />
                    </div> */}
                      <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Text variant="mediumPlus" className="catalogBottomContainer_text">
                          Unable to find what you are looking for?
                        </Text>
                        <button
                          className="buttonContainer"
                          onClick={handleGoToDashboardButtonClick}
                          tabIndex={0}
                        >
                          <span>{Constants.GO_TO_WORKSPACE_TEXT}</span>
                          <Icon iconName="Forward" className="forwardIcon" />
                        </button>
                      </div>

                    </Stack>
                  </div>
                </div>
              </Stack>
            </Suspense>
          ) : (
            <div
              style={{
                justifyContent: "center",
                alignContent: "center",
                marginLeft: "30%",
                marginRight: 30,
                width: "50%",
              }}>
              <ErrorComponent
                errorData={errorData}
                isWorkspaceValueError={false}
              />
            </div>
          )}
        </Stack>
      )
      }
    </Stack >
  );
};

export const V2_CatalogPage = injectIntl(CatalogComponent);

// Generated by Copilot
// updated on: 2025-09-19 | 4 line added | [Auto Generated] | Handled filter condition based on search | Time Taken 0 min
// updated on: 2025-11-10 | 4 line added | [Auto Generated] | Updated Padding To Add the Spaces b/w buttons | Time Taken 10 min